/**
 * AuditLogEntry Component
 * Displays a single audit log entry with event details
 */

import { Badge } from '../ui';
import { tableStyles } from '../../lib/styles';
import type { AuditLogEntry as AuditLogEntryType } from '@leal-crm/shared';

interface AuditLogEntryProps {
  entry: AuditLogEntryType;
  onViewDetails?: (entry: AuditLogEntryType) => void;
}

/**
 * Get badge color based on event category
 */
function getCategoryColor(action: string): string {
  // Success events
  if (action.includes('success') || action === 'logout' || action.includes('_added') || action.includes('_created') || action.includes('_activated')) {
    return 'green';
  }
  // Failure/security events
  if (action.includes('failed') || action.includes('locked')) {
    return 'red';
  }
  // Warning events
  if (action.includes('_removed') || action.includes('_deactivated')) {
    return 'yellow';
  }
  // Info events
  if (action.includes('_changed') || action.includes('_updated') || action.includes('_renamed') || action.includes('_reset')) {
    return 'blue';
  }
  return 'gray';
}

const ENTITY_LABELS: Record<string, string> = {
  auth: 'Auth',
  staff: 'Personal',
  booking: 'Reserva',
  customer: 'Cliente',
  vehicle: 'Vehiculo',
  setting: 'Ajustes',
  bay: 'Bahia',
  service: 'Servicio',
  template: 'Plantilla',
};

/**
 * Format timestamp to local date/time with UTC for tooltip
 */
function formatTimestamp(timestampSeconds: number): { date: string; time: string; utcDisplay: string } {
  const date = new Date(timestampSeconds * 1000);
  const utcDate = date.toUTCString();
  return {
    date: date.toLocaleDateString(),
    time: date.toLocaleTimeString(),
    utcDisplay: `UTC: ${utcDate}`,
  };
}

export function AuditLogEntry({ entry, onViewDetails }: AuditLogEntryProps) {
  const { date, time, utcDisplay } = formatTimestamp(entry.createdAt);
  const categoryColor = getCategoryColor(entry.action);
  const entityLabel = ENTITY_LABELS[entry.entityType] || entry.entityType;
  const entityId = entry.entityId ? entry.entityId.slice(0, 8) : '-';

  return (
    <tr
      className={`${tableStyles.row} cursor-pointer`}
      onClick={() => onViewDetails?.(entry)}
    >
      {/* Timestamp - shows local time with UTC on hover */}
      <td className={tableStyles.cell}>
        <div className="text-gray-900 cursor-help" title={utcDisplay}>
          {date}
        </div>
        <div className="text-[11px] text-gray-500">{time}</div>
      </td>

      {/* Actor */}
      <td className={tableStyles.cell}>
        <div className="text-sm font-medium text-gray-900">
          {entry.actorType === 'system' || entry.actorId === 'anonymous'
            ? 'Sistema'
            : entry.actorName || entry.actorEmail || entry.actorId}
        </div>
        {entry.actorType !== 'system' && entry.actorId !== 'anonymous' && entry.actorEmail && entry.actorName && (
          <div className="text-xs text-gray-500">{entry.actorEmail}</div>
        )}
      </td>

      {/* Event Type */}
      <td className={tableStyles.cell}>
        <Badge color={categoryColor}>
          {entry.actionDisplayName || entry.action}
        </Badge>
      </td>

      {/* Entity */}
      <td className={tableStyles.cell}>
        <div className="text-gray-900">{entityLabel}</div>
        <div className="text-[11px] text-gray-500 font-mono">{entityId}</div>
      </td>

      {/* IP Address */}
      <td className={`${tableStyles.cell} text-gray-500`}>
        {entry.ipAddress || '-'}
      </td>

      {/* Details indicator */}
      <td className={`${tableStyles.cell} text-right`}>
        <button
          className="text-blue-600 hover:text-blue-800 text-sm"
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails?.(entry);
          }}
        >
          Ver
        </button>
      </td>
    </tr>
  );
}
